import { cn } from "cn";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import { PropsWithChildren, ReactNode } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";
import Select from "react-select";

import type { SelectOption } from "@winshop-crm/types";
import { WSDropzone, WSTextEditor } from "@winshop-crm/ui";

import type { DropzoneFile } from "../Common/file-dropzone/file-dropzone";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { DatePicker } from "../ui/date-picker";
import { Field, FieldDescription, FieldError, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import {
  Select as UiSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { multiSelectStyles, singleSelectStyles } from "./react-select-styles";
import SelectMenuList from "./select-menu-list";

/* ---------- layout ---------- */

type SectionProps = PropsWithChildren<{
  title: string;
  action?: ReactNode;
  className?: string;
  contentClassName?: string;
}>;

/** Card w/ small title row. One section = one group of related fields. */
export const Section = ({ title, action, className, contentClassName, children }: SectionProps) => (
  <Card size="sm" className={cn("overflow-visible", className)}>
    <CardHeader className="flex flex-row items-center justify-between gap-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      {action}
    </CardHeader>
    <CardContent className={cn("flex flex-col gap-3", contentClassName)}>{children}</CardContent>
  </Card>
);

/** Responsive field grid: 1 col on phone, `cols` on sm+. */
export const FieldGrid = ({
  cols = 2,
  className,
  children,
}: PropsWithChildren<{ cols?: 1 | 2 | 3; className?: string }>) => (
  <div
    className={cn(
      "grid grid-cols-1 gap-3",
      cols === 2 && "sm:grid-cols-2",
      cols === 3 && "sm:grid-cols-2 xl:grid-cols-3",
      className
    )}
  >
    {children}
  </div>
);

/* ---------- bound field core ---------- */

/** Props every RHF-bound field takes. `name` is a `Path<T>` so template names need a cast. */
export type Bound<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  readOnly?: boolean;
  className?: string;
};

type RenderArgs<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  /** `fieldState.invalid` unless readOnly — read-only fields never show errors. */
  invalid: boolean;
};

const labelClass = "text-xs text-muted-foreground";

/** Controller + shadcn Field chrome (label above, error below). */
const BoundField = <T extends FieldValues>({
  control,
  name,
  label,
  readOnly,
  className,
  children,
}: Bound<T> & { children: (args: RenderArgs<T>) => ReactNode }) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const invalid = !readOnly && fieldState.invalid;
      return (
        <Field data-invalid={invalid || undefined} className={cn("min-w-0 gap-1.5", className)}>
          <FieldLabel htmlFor={name} className={labelClass}>
            {label}
          </FieldLabel>
          {children({ field, fieldState, invalid })}
          {invalid ? <FieldError errors={[fieldState.error]} className="text-xs" /> : null}
        </Field>
      );
    }}
  />
);

/* ---------- input ---------- */

type FormInputProps<T extends FieldValues> = Bound<T> & {
  type?: "email" | "number" | "tel" | "text" | "password";
  placeholder?: string;
  suffix?: string;
  autoComplete?: string;
  /** Absolutely-positioned trailing slot (e.g. password eye toggle). */
  endAdornment?: ReactNode;
};

export const FormInput = <T extends FieldValues>({
  type = "text",
  placeholder,
  suffix,
  autoComplete,
  endAdornment,
  ...bound
}: FormInputProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => (
      <div className="relative">
        <Input
          id={bound.name}
          name={field.name}
          ref={field.ref}
          type={type}
          value={field.value ?? ""}
          onChange={field.onChange}
          onBlur={field.onBlur}
          readOnly={bound.readOnly}
          disabled={field.disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={invalid || undefined}
          className={cn(
            bound.readOnly && "bg-muted/40 text-foreground",
            (suffix || endAdornment) && "pr-10",
            type === "number" && "tabular-nums"
          )}
        />
        {suffix ? (
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-muted-foreground">
            {suffix}
          </span>
        ) : null}
        {endAdornment ? (
          <span className="absolute inset-y-0 right-2 flex items-center">{endAdornment}</span>
        ) : null}
      </div>
    )}
  </BoundField>
);

/* ---------- textarea ---------- */

type FormTextareaProps<T extends FieldValues> = Bound<T> & {
  placeholder?: string;
  rows?: number;
};

export const FormTextarea = <T extends FieldValues>({
  placeholder,
  rows,
  ...bound
}: FormTextareaProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => (
      <Textarea
        id={bound.name}
        name={field.name}
        ref={field.ref}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        readOnly={bound.readOnly}
        disabled={field.disabled}
        placeholder={placeholder}
        rows={rows}
        aria-invalid={invalid || undefined}
        className={cn(bound.readOnly && "bg-muted/40 text-foreground")}
      />
    )}
  </BoundField>
);

/* ---------- select (Base UI) ---------- */

type Option = { id: string | number; name: string };

type FormSelectProps<T extends FieldValues> = Bound<T> & {
  options: Option[];
  allowEmpty?: boolean;
  placeholder?: string;
};

/** Value in form is `number | undefined`. Base UI wants `string | null`. */
export const FormSelect = <T extends FieldValues>({
  options,
  allowEmpty = true,
  placeholder = "Nevybráno",
  ...bound
}: FormSelectProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => {
      const raw = field.value as number | string | null | undefined;
      const numeric = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
      // API uses 0 / negative ids as "not set" — treat them as empty.
      const current = Number.isNaN(numeric) || numeric <= 0 ? null : String(numeric);
      const items = [
        ...(allowEmpty ? [{ value: null, label: placeholder }] : []),
        ...options.map((o) => ({ value: String(o.id), label: o.name })),
      ];
      return (
        <UiSelect
          items={items}
          value={current}
          disabled={bound.readOnly || field.disabled}
          onValueChange={(next) => {
            field.onChange(next === null || next === "" ? undefined : Number(next));
            field.onBlur();
          }}
        >
          <SelectTrigger
            id={bound.name}
            ref={field.ref}
            aria-invalid={invalid || undefined}
            className={cn("w-full", bound.readOnly && "disabled:opacity-100 disabled:bg-muted/40")}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {allowEmpty && <SelectItem value={null}>{placeholder}</SelectItem>}
            {options.map((o) => (
              <SelectItem key={o.id} value={String(o.id)}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </UiSelect>
      );
    }}
  </BoundField>
);

/* ---------- date ---------- */

type FormDateProps<T extends FieldValues> = Bound<T> & { clearable?: boolean };

/** Value in form is `Date | null | undefined`. Read-only renders plain text. */
export const FormDate = <T extends FieldValues>({
  clearable = false,
  ...bound
}: FormDateProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => {
      const value = field.value as Date | string | null | undefined;
      if (bound.readOnly) {
        return (
          <p className="flex h-8 items-center text-sm tabular-nums">
            {value ? (
              format(new Date(value), "d. M. yyyy", { locale: cs })
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </p>
        );
      }
      return (
        <DatePicker
          id={bound.name}
          value={value}
          onChange={(d) => field.onChange(d)}
          onBlur={field.onBlur}
          clearable={clearable}
          disabled={field.disabled}
          invalid={invalid}
        />
      );
    }}
  </BoundField>
);

/* ---------- checkbox ---------- */

type FormCheckboxProps<T extends FieldValues> = Bound<T> & { description?: string };

export const FormCheckbox = <T extends FieldValues>({
  control,
  name,
  label,
  readOnly,
  className,
  description,
}: FormCheckboxProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const invalid = !readOnly && fieldState.invalid;
      return (
        <Field
          orientation="horizontal"
          data-invalid={invalid || undefined}
          className={cn("w-auto items-center gap-2", className)}
        >
          <Checkbox
            id={name}
            name={field.name}
            ref={field.ref}
            checked={Boolean(field.value)}
            onCheckedChange={(checked) => field.onChange(checked)}
            onBlur={field.onBlur}
            disabled={readOnly || field.disabled}
            aria-invalid={invalid || undefined}
          />
          <div className="flex flex-col gap-0.5">
            <FieldLabel htmlFor={name} className="text-sm font-normal">
              {label}
            </FieldLabel>
            {description ? (
              <FieldDescription className="text-xs">{description}</FieldDescription>
            ) : null}
            {invalid ? <FieldError errors={[fieldState.error]} className="text-xs" /> : null}
          </div>
        </Field>
      );
    }}
  />
);

/* ---------- react-select ---------- */

type FormAutocompleteProps<T extends FieldValues> = Bound<T> & {
  options: readonly SelectOption[];
  placeholder?: string;
};

/** Single react-select; form value is the option's numeric `value` (or undefined). */
export const FormAutocomplete = <T extends FieldValues>({
  options,
  placeholder = "",
  ...bound
}: FormAutocompleteProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => (
      <Select<SelectOption, false>
        inputId={bound.name}
        name={field.name}
        options={options}
        value={options.find((o) => o.value === field.value) ?? null}
        onChange={(selected) => field.onChange(selected?.value)}
        onBlur={field.onBlur}
        isDisabled={bound.readOnly || field.disabled}
        components={{ MenuList: SelectMenuList }}
        styles={singleSelectStyles(invalid)}
        closeMenuOnScroll
        placeholder={placeholder}
        backspaceRemovesValue
        isClearable
        menuPlacement="auto"
      />
    )}
  </BoundField>
);

type FormMultiSelectProps<T extends FieldValues> = Bound<T> & {
  options: readonly SelectOption[];
  placeholder?: string;
};

/** Multi react-select; form value is `number[]`. Chips derived from options each render. */
export const FormMultiSelect = <T extends FieldValues>({
  options,
  placeholder = "",
  ...bound
}: FormMultiSelectProps<T>) => (
  <BoundField {...bound}>
    {({ field, invalid }) => {
      const ids = (field.value ?? []) as number[];
      return (
        <Select<SelectOption, true>
          isMulti
          inputId={bound.name}
          name={field.name}
          options={options}
          value={options.filter((o) => ids.includes(o.value))}
          onChange={(selected) => field.onChange(selected.map((o) => o.value))}
          onBlur={field.onBlur}
          isDisabled={bound.readOnly || field.disabled}
          components={{ MenuList: SelectMenuList }}
          styles={multiSelectStyles(invalid)}
          closeMenuOnScroll
          placeholder={placeholder}
          backspaceRemovesValue
          isClearable
          menuPlacement="auto"
        />
      );
    }}
  </BoundField>
);

/* ---------- rich text ---------- */

type FormEditorProps<T extends FieldValues> = Omit<Bound<T>, "label"> & {
  /** Optional — detail sections already carry a title. */
  label?: string;
  placeholder?: string;
};

export const FormEditor = <T extends FieldValues>({
  control,
  name,
  label,
  readOnly,
  className,
  placeholder,
}: FormEditorProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const invalid = !readOnly && fieldState.invalid;
      return (
        <Field data-invalid={invalid || undefined} className={cn("min-w-0 gap-1.5", className)}>
          {label ? <FieldLabel className={labelClass}>{label}</FieldLabel> : null}
          <WSTextEditor
            value={(field.value as string | undefined) ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={invalid}
            isReadOnly={readOnly}
            placeholder={placeholder}
          />
          {invalid ? <FieldError errors={[fieldState.error]} className="text-xs" /> : null}
        </Field>
      );
    }}
  />
);

/* ---------- files ---------- */

type FormDropzoneProps<T extends FieldValues> = Omit<Bound<T>, "label" | "readOnly"> & {
  label?: string;
  maxNumberOfFiles?: number;
};

/** Form value is `DropzoneFile[]`. */
export const FormDropzone = <T extends FieldValues>({
  control,
  name,
  label,
  className,
  maxNumberOfFiles,
}: FormDropzoneProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <Field
        data-invalid={fieldState.invalid || undefined}
        className={cn("min-w-0 gap-1.5", className)}
      >
        {label ? <FieldLabel className={labelClass}>{label}</FieldLabel> : null}
        <WSDropzone
          values={(field.value as DropzoneFile[] | undefined) ?? []}
          onChange={field.onChange}
          maxNumberOfFiles={maxNumberOfFiles}
        />
        {fieldState.invalid ? <FieldError errors={[fieldState.error]} className="text-xs" /> : null}
      </Field>
    )}
  />
);
